import { DIRECT_BACKEND_ORIGIN, PLATFORM_API_BASE_URL } from '@/lib/apiClient';
import { getSystemMetrics, type InfrastructureSnapshot } from '@/api/systemMetrics.api';

export interface CoreHealthResponse {
    status: 'healthy' | 'degraded' | 'error';
    timestamp: string;
    uptime: number;
    version: string;
    checks: {
        database: {
            status: 'healthy' | 'unhealthy';
            error?: string;
        };
        storage: {
            status: 'healthy' | 'unhealthy';
            error?: string;
        };
        llm: {
            status: 'healthy' | 'unhealthy';
            error?: string;
        };
    };
}

export interface PlatformPingResponse {
    status: string;
    namespace: string;
    timestamp: string;
}

export interface ServiceHealthResponse {
    status: 'healthy' | 'unhealthy';
    responseTime?: string;
    provider?: string;
    timestamp: string;
    error?: string;
}

/**
 * Health endpoints live on the backend host, not the SPA host — derive
 * the origin from the same base URL the platform apiClient resolves
 * (relative '/api/v1/platform' in dev keeps the Vite proxy working).
 * Health checks are public and non-credentialed.
 */
// In production the platform API is same-origin (Pages proxy), but /api/health
// is outside /api/v1 and public, so it goes straight to Render.
const BACKEND_ORIGIN = import.meta.env.PROD && PLATFORM_API_BASE_URL.startsWith('/')
    ? DIRECT_BACKEND_ORIGIN
    : new URL(PLATFORM_API_BASE_URL, window.location.origin).origin;

async function getJson<T>(path: string): Promise<T> {
    const response = await fetch(`${BACKEND_ORIGIN}${path}`, {
        headers: { Accept: 'application/json' },
    });
    const text = await response.text();
    if (!text) {
        throw new Error(`Health request failed: ${response.status}`);
    }
    return JSON.parse(text) as T;
}

export async function getCoreHealth(): Promise<CoreHealthResponse> {
    return getJson<CoreHealthResponse>('/api/health');
}

export async function getPlatformPing(): Promise<PlatformPingResponse> {
    return getJson<PlatformPingResponse>('/api/v1/platform/_ping');
}

export async function getDashboardHealthSnapshot(): Promise<{
    core: CoreHealthResponse;
    ping: PlatformPingResponse;
    database: ServiceHealthResponse;
    supabase: ServiceHealthResponse;
    llm: ServiceHealthResponse;
    infrastructure: InfrastructureSnapshot;
}> {
    const [core, ping, database, supabase, llm, metrics] = await Promise.all([
        getCoreHealth(),
        getPlatformPing(),
        getJson<ServiceHealthResponse>('/api/health/database'),
        getJson<ServiceHealthResponse>('/api/health/supabase'),
        getJson<ServiceHealthResponse>('/api/health/llm'),
        getSystemMetrics('1h'),
    ]);
    return { core, ping, database, supabase, llm, infrastructure: metrics.infrastructure };
}
